import type { Elemento } from '../ai/esquema';
import { todosOsElementos } from './referencia';

export const MAX_LADO = 1600;

/** Cabe a imagem num quadrado de `max`, sem NUNCA aumentar. */
export function fitDentro(width: number, height: number, max = MAX_LADO): { width: number; height: number } {
	const maior = Math.max(width, height);
	if (maior <= max || maior === 0) return { width, height };

	const fator = max / maior;
	return { width: Math.round(width * fator), height: Math.round(height * fator) };
}

/** Tamanho aproximado, em KB, de uma data URL — para mostrar e para logar. */
export function pesoKB(dataUrl: string): number {
	const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
	return Math.round((base64.length * 3) / 4 / 1024);
}

export function ehImagem(file: { type?: string } | null | undefined): boolean {
	return !!file?.type?.startsWith('image/');
}

/** Valor de atributo que é imagem embutida — não cabe num campo de texto. */
export function ehEmbutida(value: string | null | undefined): boolean {
	return !!value && value.startsWith('data:image/');
}

function hex(n: number): string {
	return n.toString(16).padStart(2, '0');
}

export function paletaDe(pixels: ArrayLike<number>, max = 6): Array<{ cor: string; parte: number }> {
	const contagem = new Map<number, { r: number; g: number; b: number; n: number }>();
	let total = 0;

	for (let i = 0; i < pixels.length; i += 4) {
		if (pixels[i + 3] < 128) continue; // transparente não conta
		const r = pixels[i];
		const g = pixels[i + 1];
		const b = pixels[i + 2];
		const chave = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);

		const balde = contagem.get(chave) ?? { r: 0, g: 0, b: 0, n: 0 };
		balde.r += r;
		balde.g += g;
		balde.b += b;
		balde.n++;
		contagem.set(chave, balde);
		total++;
	}

	if (!total) return [];

	return [...contagem.values()]
		.sort((a, b) => b.n - a.n)
		.slice(0, max)
		.filter((c) => c.n / total >= 0.02)
		// a média do balde devolve o tom real, não o canto do balde
		.map((c) => ({
			cor: `#${hex(Math.round(c.r / c.n))}${hex(Math.round(c.g / c.n))}${hex(Math.round(c.b / c.n))}`,
			parte: Math.round((c.n / total) * 100),
		}));
}

export interface Preparada {
	dataUrl: string;
	/** cores medidas, da mais presente para a menos */
	palette: Array<{ cor: string; parte: number }>;
}

export function prepararImagem(file: Blob, max = MAX_LADO): Promise<Preparada> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const img = new Image();

		img.onload = () => {
			URL.revokeObjectURL(url);
			try {
				const { width, height } = fitDentro(img.naturalWidth, img.naturalHeight, max);
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;

				const ctx = canvas.getContext('2d');
				if (!ctx) throw new Error('sem contexto 2d');
				ctx.drawImage(img, 0, 0, width, height);

				const palette = paletaDe(ctx.getImageData(0, 0, width, height).data);
				resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.85), palette });
			} catch (e) {
				reject(e);
			}
		};

		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('não consegui ler essa imagem'));
		};

		img.src = url;
	});
}

/** Carrega uma data URL como <img>, para poder desenhar de novo no canvas. */
function carregar(dataUrl: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('referência ilegível'));
		img.src = dataUrl;
	});
}

/** Máximo do lado maior de um recorte: acima disso o código vira ilegível. */
export const MAX_RECORTE = 900;

/** Troca `src="foto1"` pelo que a foto virou de verdade. */
function trocaId(code: string, id: string, url: string): string {
	return code.split(`"${id}"`).join(`"${url}"`).split(`'${id}'`).join(`'${url}'`);
}

export async function aplicaFotos(code: string, elementos: Elemento[], referencia: string | null): Promise<string> {
	// a árvore inteira: com fluxo, a foto costuma estar dentro de um card
	const fotos = todosOsElementos(elementos).filter((e) => e.tipo === 'foto');
	if (!fotos.length) return code;

	// sem referência (ou ela ilegível), o id viraria src quebrado na tela
	let img: HTMLImageElement | null = null;
	if (referencia) {
		try {
			img = await carregar(referencia);
		} catch {
			img = null;
		}
	}

	let out = code;
	for (const foto of fotos) {
		const [, , largura, altura] = foto.box;

		if (!img || !foto.crop) {
			out = trocaId(out, foto.id, `https://picsum.photos/seed/${foto.id}/${Math.max(1, Math.round(largura))}/${Math.max(1, Math.round(altura))}`);
			continue;
		}

		const [x0, y0, x1, y1] = foto.crop;
		const sx = Math.round(x0 * img.naturalWidth);
		const sy = Math.round(y0 * img.naturalHeight);
		const sw = Math.max(1, Math.round((x1 - x0) * img.naturalWidth));
		const sh = Math.max(1, Math.round((y1 - y0) * img.naturalHeight));
		const { width, height } = fitDentro(sw, sh, MAX_RECORTE);

		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext('2d');
		if (!ctx) continue;

		ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
		out = trocaId(out, foto.id, canvas.toDataURL('image/jpeg', 0.8));
	}

	return out.replace(/(["'])foto\d+\1/g, (_, aspas: string) => `${aspas}https://picsum.photos/seed/bloco/800/600${aspas}`);
}
